import React, { Suspense } from 'react';
import ReactDOM from 'react-dom';
import whyDidYouRender from '@welldone-software/why-did-you-render';
import { ApolloProvider } from '@apollo/client';
import { Provider as ReduxProvider } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { createMuiTheme, ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import { DndProvider } from 'react-dnd';
import { TouchBackend } from 'react-dnd-touch-backend';
import { Unleash } from '@twigeducation/unleash-client-react';
import { ConfigCatProvider } from 'configcat-react';
import client from './services/apolloClient';
import store from './services/reduxStore';
import Routes from './routes';
import LoadingPlaceholder from './components/LoadingPlaceholder';
import theme from '../../tap-fe-components/src/theme';
import focusHandler from '../../tap-fe-components/src/focus-handler';
import proxyLocalStorage from './helpers/proxyLocalStorage';
import { getUserId } from './helpers/tokenHelper/user';

proxyLocalStorage();

if (window.config.TRACK_COMPONENT_RE_RENDERS) {
    whyDidYouRender(React, {
        onlyLogs: false,
        titleColor: 'green',
        diffNameColor: 'aqua',
        trackAllPureComponents: true,
    });
}
const muiTheme = createMuiTheme({
    palette: {
        primary: {
            main: theme.accentColor,
        },
        action: {
            disabled: 'rgba(22, 115, 74, 1)',
            opacity: 0.5,
        },
    },
    overrides: {
        MuiButton: {
            padding: '0 12px',
            outlinedPrimary: {
                borderColor: theme.accentColor,
                '&$disabled': {
                    borderColor: 'rgba(27, 131, 103, 0.12)',
                },
            },
        },
        MuiIconButton: {
            root: {
                textTransform: 'none',
                '&$disabled': {
                    opacity: '0.5',
                },
            },
        },
        MuiOutlinedInput: {
            root: {
                '&$focused $notchedOutline': {
                    borderWidth: 1,
                },
            },
        },
    },
});

focusHandler().attachListeners();

const configCatSdkKey = window.config.CONFIGCAT_SDK_KEY;
const isConfigCatKeyValid = configCatSdkKey && !configCatSdkKey.startsWith('#');

const ConfigCatWrapper = isConfigCatKeyValid
    ? ({ children }) => (
          <ConfigCatProvider sdkKey={configCatSdkKey} options={{ pollIntervalSeconds: 60 }}>
              {children}
          </ConfigCatProvider>
      )
    : ({ children }) => children;

ReactDOM.render(
    <ApolloProvider client={client}>
        <ReduxProvider store={store}>
            <Suspense fallback={<LoadingPlaceholder />}>
                <ConfigCatWrapper>
                    <Unleash
                        clientId={window.config.UNLEASH_CLIENT_ID}
                        user={{ key: getUserId() }}
                        url={window.config.UNLEASH_URL}
                        value={{ 'twig-drawing-tool-stamp-icon': true }}
                    >
                        <ThemeProvider theme={theme}>
                            <MuiThemeProvider theme={muiTheme}>
                                <DndProvider backend={TouchBackend} options={{ enableMouseEvents: true }}>
                                    <Routes />
                                </DndProvider>
                            </MuiThemeProvider>
                        </ThemeProvider>
                    </Unleash>
                </ConfigCatWrapper>
            </Suspense>
        </ReduxProvider>
    </ApolloProvider>,
    document.getElementById('app'),
);
